import { Just, Maybe, Nothing } from 'core/maybe';
import { error as resultError, ok as resultOk, Result } from 'core/result';
import { Loading } from 'core/state';
import { Loaded } from 'stores/core';
import { unreachable } from 'utils/unreachable';

export type WidgetState<T, E> = Loading | Loaded<Result<Maybe<T>, E>>;

const loadingConstructor = <T, E>(): WidgetState<T, E> => Loading();
const data = <T, E>(value: T): WidgetState<T, E> =>
  Loaded(resultOk(Just(value)));
const noData = <T, E>(): WidgetState<T, E> => Loaded(resultOk(Nothing()));
const errorConstructor = <T, E>(e: E): WidgetState<T, E> =>
  Loaded(resultError(e));

const mapResult =
  <T, E, R>(onOk: (value: T) => R, onError: (err: E) => R) =>
  (result: Result<T, E>) => {
    switch (result[0]) {
      case 'ok':
        return onOk(result[1]);
      case 'error':
        return onError(result[1]);
      default:
        return unreachable(result);
    }
  };

const map =
  <T, E, R>(
    onData: (t: T) => R,
    loading?: () => R,
    onNoData?: () => R,
    onError?: (e: E) => R,
  ) =>
  (state: WidgetState<T, E>): R | undefined => {
    switch (state[0]) {
      case 'loading':
        return loading?.();
      case 'loaded':
        return mapResult(
          (value: Maybe<T>) => {
            switch (value[0]) {
              case 'just':
                return onData(value[1]);
              case 'nothing':
                return onNoData?.();
              default:
                return unreachable(value);
            }
          },
          (e: E) => onError?.(e),
        )(state[1]);
        break;
      default:
        return unreachable(state);
    }
  };

export const WidgetState = {
  loading: loadingConstructor,
  data,
  noData,
  error: errorConstructor,
  map,
};
